import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const SENTRY_ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'development';
const IS_PRODUCTION = import.meta.env.PROD;

/**
 * Initialize Sentry for frontend error tracking and performance monitoring.
 * Call this as early as possible in main.tsx.
 */
export function initializeSentry(): void {
  // Hosted billing links carry a short-lived capability in the URL fragment.
  // Do not initialize tracing/replay/error capture on this standalone flow.
  if (window.location.pathname === '/partner-card-setup' || window.location.pathname.startsWith('/partner-onboarding')) return;
  if (!SENTRY_DSN) {
    console.log('Sentry disabled (no VITE_SENTRY_DSN configured)');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,

    // Performance monitoring. Off outside production: local traces are noise,
    // and they cost quota.
    // Starting deliberately low; dial up once the volume is understood.
    tracesSampleRate: IS_PRODUCTION ? 0.001 : 0, // 0.1% in prod, off in dev

    // No Session Replay. It ran with text unmasked, recording patient names,
    // DOBs, prescriptions and lab results to Sentry. If it comes back, keep
    // Sentry's default masking and exclude /dtp and /patients pages.

    // Integrations
    integrations: [
      // Browser tracing for performance
      Sentry.browserTracingIntegration(),
      // Capture console.error calls as Sentry errors
      Sentry.captureConsoleIntegration({
        levels: ['error'], // Only capture console.error, not warn/log
      }),
    ],

    // Filter out noisy errors
    ignoreErrors: [
      // Browser extensions
      'chrome-extension://',
      'moz-extension://',
      // Common user-caused errors
      'ResizeObserver loop',
      // Cancelled navigation
      'AbortError',
    ],

    // Don't send errors from localhost in production builds
    beforeSend(event, _hint) {
      // Search terms can carry clinic names, emails and NPIs — redact before
      // the event leaves, in every environment, not just production.
      if (event.request?.url) {
        event.request.url = redactSearchTerm(event.request.url);
      }
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(b => (
          b.data && (b.data.to || b.data.from)
            ? { ...b, data: { ...b.data, to: redactSearchTerm(b.data.to), from: redactSearchTerm(b.data.from) } }
            : b
        ));
      }

      // Always allow errors in development
      if (!IS_PRODUCTION) return event;

      // In production, still send localhost errors for debugging
      return event;
    },

    // Navigation crumbs are recorded as they happen, ahead of any error.
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data?.to || breadcrumb.data?.from) {
        breadcrumb.data = {
          ...breadcrumb.data,
          to: redactSearchTerm(breadcrumb.data.to),
          from: redactSearchTerm(breadcrumb.data.from)
        };
      }
      return breadcrumb;
    },

    // Add release version
    release: import.meta.env.VITE_APP_VERSION || '1.0.0',
  });

  console.log(`Sentry initialized (env: ${SENTRY_ENVIRONMENT})`);
}

/**
 * Set user context for Sentry.
 * Call this after user login to associate errors with users.
 */

/**
 * Strip the free-text search term out of any URL before it leaves the browser.
 *
 * The Users directory keeps its filters in the query string so a view can be
 * shared, and `q` is a search box people type clinic names, emails and NPIs
 * into. Sentry attaches the full URL to every error and to navigation
 * breadcrumbs, and errors are not sampled — so without this, searching for a
 * clinic by email and hitting any unrelated error posts that email to a
 * third-party service.
 *
 * Only `q` is redacted. The other filters (role, state, sort, page) are closed
 * vocabularies with no personal data in them, and they are the ones worth
 * keeping legible when reading a stack trace.
 */
function redactSearchTerm(url?: string): string | undefined {
  if (url?.includes('/partner-card-setup')) return url.split(/[?#]/, 1)[0];
  if (!url || !url.includes('q=')) return url;
  try {
    // Relative URLs appear in breadcrumbs; give the parser a base to work from.
    const parsed = new URL(url, window.location.origin);
    if (!parsed.searchParams.has('q')) return url;
    parsed.searchParams.set('q', '[redacted]');
    return url.startsWith('http') ? parsed.toString() : parsed.pathname + parsed.search;
  } catch {
    // Never let scrubbing throw — a malformed URL must not lose the error.
    return url.replace(/([?&]q=)[^&]*/g, '$1[redacted]');
  }
}

export function setSentryUser(user: {
  id: string;
  email?: string;
  role?: string;
}): void {
  if (!SENTRY_DSN) return;

  Sentry.setUser({
    id: user.id,
    email: user.email,
  });

  if (user.role) {
    Sentry.setTag('user.role', user.role);
  }
}

/**
 * Clear user context (call on logout)
 */
export function clearSentryUser(): void {
  if (!SENTRY_DSN) return;
  Sentry.setUser(null);
}

/**
 * Set tenant context from branding/config
 */
export function setSentryTenant(tenant: {
  id?: string;
  name?: string;
}): void {
  if (!SENTRY_DSN) return;

  if (tenant.id) {
    Sentry.setTag('tenant.id', tenant.id);
  }
  if (tenant.name) {
    Sentry.setTag('tenant.name', tenant.name);
  }
}

/**
 * Capture an exception manually
 */
export function captureException(
  error: Error,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, any>;
  }
): string | undefined {
  if (!SENTRY_DSN) {
    console.error('Error (Sentry disabled):', error);
    return undefined;
  }

  return Sentry.captureException(error, {
    tags: context?.tags,
    extra: context?.extra,
  });
}

/**
 * Capture a message
 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info'
): string | undefined {
  if (!SENTRY_DSN) return undefined;

  return Sentry.captureMessage(message, level);
}

/**
 * Sentry Error Boundary component for React
 * Wrap your app or specific sections with this
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

/**
 * HOC to wrap components with Sentry error boundary
 */
export const withSentryErrorBoundary = Sentry.withErrorBoundary;

/**
 * Test function to trigger a Sentry error for verification
 * Can be called from browser console: window.testSentry()
 */
export function triggerTestError(): string | undefined {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const env = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'development';

  if (!dsn) {
    console.error('Sentry test failed: VITE_SENTRY_DSN not configured');
    return undefined;
  }

  const testError = new Error(`[Sentry Test] Frontend test error triggered at ${new Date().toISOString()}`);
  const eventId = Sentry.captureException(testError, {
    extra: {
      test: true,
      triggeredBy: 'manual-test',
      environment: env
    }
  });

  console.log(`🔴 Sentry test error sent (eventId: ${eventId})`);
  return eventId;
}

// Expose test function globally for browser console access
if (typeof window !== 'undefined') {
  (window as any).testSentry = triggerTestError;
}

// Re-export Sentry for advanced usage
export { Sentry };
